import { useMemo } from "react";
import { useSelector } from "react-redux";

import formatUrlKey from "../helpers/FormatUrlKey";
import { excludedGiftVoucherSiteIds } from "../helpers/giftVoucherVenueExclusions";
import mathHelpers from "../helpers/math";
import { ICurrency } from "../interfaces/ICurrency";
import { SiteStatusType } from "../interfaces/ISite";
import { IApplicationState } from "../store";

// COPIED FROM GLOBAL WEBSITE
// Please keep changes syncronised between projects
// todo - find a better way to share react / typescript / javascript utils between projects



// Key is the normalised, lower case, no spaces version of the country name, eg "dallas-fort-worth"
// Value is the plain text display value, eg "Dallas - Fort Worth"
export interface LocationEntry {
    key: string;
    value: string;
}

export interface VenueEntry {
    key: string;    // venueUrlKey
    value: string;  // venue name
    id: number;     // venue Id
    code: string;   // venue Code
    address1: string;
    address2: string;
    address3: string;
    distanceInMeters: number | undefined;
}

export interface ServiceAreaEntry {
    areaData: LocationEntry;
    venues: VenueEntry[];
}

export interface StateEntry {
    stateData: LocationEntry;
    serviceAreas: Record<string, ServiceAreaEntry>;
}

export interface CountryEntry {
    countryData: LocationEntry;
    states: Record<string, StateEntry>;
}

export interface RegionEntry {
    regionData: LocationEntry;
    countries: Record<string, CountryEntry>;
}

// a nested records object thing is apparently better for look ups but doesn't preserve sort order for rendering
export type NestedLocationMap = Record<string, RegionEntry>;

const useLocationsWithVenues = (currencyFilter?: ICurrency | undefined, filterForRegionalGiftVouchers?: boolean | undefined) => {
    const allVenues = useSelector((s: IApplicationState) => s.sites.sites);
    const location = useSelector((state: IApplicationState) => state.location.location);

    return useMemo(() => {
        const regionMap = new Map<string, string>();
        const countryMap = new Map<string, string>();
        const stateMap = new Map<string, string>();
        const areaMap = new Map<string, string>();

        const nestedMap: NestedLocationMap = {};

        const venuesToSort = (allVenues ?? []).filter(v => {
            //Filter out the opt-out sites:
            if (currencyFilter !== undefined && excludedGiftVoucherSiteIds.includes(v.siteId)) return false;

            //Apply filtering to hide venues not relavent to regional-gift-vouchers from showing on the redeem locations lists
            if (filterForRegionalGiftVouchers === true) {
                //filter out external booking venues, which don't accept out gift vouchers
                if (v?.useExternalBookingUrl) return false;

                //filter out "reservation only" venues, where no purchase is being made to apply a voucher to
                if (v?.reservationPortal) return false;

                //filter out venues in unready states eg 'inactive'
                if (v?.siteStatusTypeId !== SiteStatusType.Active && v?.siteStatusTypeId !== SiteStatusType.Deploying) {
                    return false;
                }
            }

            // filter out non matching currency venues if requested
            if (currencyFilter !== undefined) return v?.currencyId === (currencyFilter?.id ?? -1);

            //otherwise return everything
            return true;
            
        });

        venuesToSort
            ?.forEach(venue => {

                if (!venue.country || !venue.state || !venue.serviceArea) {
                    console.warn(`Venue ${venue.code} ${venue.siteId} in UseLocationsWithVenues hook is missing key property: country:${venue.country}, state:${venue.state}, serviceArea:${venue.serviceArea}`);
                    // Incomplete venues will be catagorised under the '-' label at every missing level
                }

                const regionKey = formatUrlKey(venue.seoRegion) ?? "-";
                const countryKey = formatUrlKey(venue.country) ?? "-";
                const stateKey = formatUrlKey(venue.state) ?? "-";
                const areaKey = formatUrlKey(venue.serviceArea) ?? "-";

                //flat maps of what country, state or serviceArea keys are valid
                if (!regionMap.has(regionKey)) regionMap.set(regionKey, venue.seoRegion);
                if (!countryMap.has(countryKey)) countryMap.set(countryKey, venue.country);
                if (!stateMap.has(stateKey)) stateMap.set(stateKey, venue.state);
                if (!areaMap.has(areaKey)) areaMap.set(areaKey, venue.serviceArea);

                // Region level
                if (!nestedMap[regionKey]) {
                    nestedMap[regionKey] = {
                        regionData: { key: regionKey, value: venue.seoRegion },
                        countries: {}
                    };
                }

                // Country level
                const regionEntry = nestedMap[regionKey];
                if (!regionEntry.countries[countryKey]) {
                    regionEntry.countries[countryKey] = {
                        countryData: { key: countryKey, value: venue.country },
                        states: {}
                    };
                }

                // State level
                const countryEntry = regionEntry.countries[countryKey];
                if (!countryEntry.states[stateKey]) {
                    countryEntry.states[stateKey] = {
                        stateData: { key: stateKey, value: venue.state },
                        serviceAreas: {}
                    };
                }

                // Service area level
                const stateEntry = countryEntry.states[stateKey];
                if (!stateEntry.serviceAreas[areaKey]) {
                    stateEntry.serviceAreas[areaKey] = {
                        areaData: { key: areaKey, value: venue.serviceArea },
                        venues: []
                    };
                }

                const serviceAreaEntry = stateEntry.serviceAreas[areaKey];

                // Avoid duplicate venues by ID
                if (!serviceAreaEntry.venues.some(v => v.id === venue.siteId)) {
                    serviceAreaEntry.venues.push({
                        key: venue.urlKey,
                        value: venue.name,
                        id: venue.siteId,
                        code: venue.code,
                        address1: venue?.siteMetadata?.addressLine1,
                        address2: venue?.siteMetadata?.addressLine2,
                        address3: venue?.siteMetadata?.addressLine3,
                        distanceInMeters: location ? mathHelpers.distanceBetweenPoints(
                            location?.latitude, location?.longitude, venue.siteMetadata.latitude!, venue.siteMetadata.longitude!
                        ) : undefined,
                    });
                }

            });

        //flatted out a list and just give us the top level items, (eg the name of every country we know about)
        const mapToList = (map: Map<string, string>): LocationEntry[] =>
            Array.from(map.entries())
                .map(([key, value]) => ({ key, value }))
                .sort((a, b) => a.key.localeCompare(b.key));

        //check if a string matches any of the location data points we know about
        const isLocationValid = (
            regionKey?: string,
            countryKey?: string,
            stateKey?: string,
            areaKey?: string
        ): boolean => {
            //check for valid region
            if (!regionKey) return false;
            const region = nestedMap[regionKey];
            if (!region) return false;

            //check for valid country
            if (!countryKey) return false;
            const country = region.countries[countryKey];
            if (!country) return false;

            //check for valid state if required
            if (!stateKey) return true;
            const state = country.states[stateKey];
            if (!state) return false;

            // check for valid service area if required
            if (!areaKey) return true;
            const area = state.serviceAreas[areaKey];
            return !!area;
        };

        return {
            regions: mapToList(regionMap),
            countries: mapToList(countryMap),
            states: mapToList(stateMap),
            serviceAreas: mapToList(areaMap),
            nestedMap,
            isLocationValid
        };
    }, [allVenues, location, currencyFilter, filterForRegionalGiftVouchers]);
};

export default useLocationsWithVenues;
